import { useCallback, useEffect, useState } from "react";

/** idle → copied | failed → idle again after a moment. */
export type CopyStatus = "idle" | "copied" | "failed";

const STATUS_SHOWN_FOR_MS = 1500;

/** Writes `text` to the clipboard; never throws. A missing clipboard (no secure context) fails. */
export async function copyText(
  text: string,
  clipboard: Pick<Clipboard, "writeText"> | undefined,
): Promise<Exclude<CopyStatus, "idle">> {
  if (clipboard === undefined) {
    return "failed";
  }
  try {
    await clipboard.writeText(text);
    return "copied";
  } catch {
    return "failed";
  }
}

/** Copies text and reports the outcome for a short while, then returns to idle. */
export function useCopyToClipboard(): { status: CopyStatus; copy: (text: string) => void } {
  const [status, setStatus] = useState<CopyStatus>("idle");
  useEffect(() => {
    if (status === "idle") {
      return undefined;
    }
    const timer = setTimeout(() => { setStatus("idle"); }, STATUS_SHOWN_FOR_MS);
    return () => { clearTimeout(timer); };
  }, [status]);
  const copy = useCallback((text: string) => {
    // `navigator.clipboard` is undefined outside a secure context, whatever the DOM types say.
    const clipboard = navigator.clipboard as Clipboard | undefined;
    void copyText(text, clipboard).then(setStatus);
  }, []);
  return { status, copy };
}
